import React, { useState, useCallback, useMemo } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectCapitalFinancing,
  ConnectCapitalFinancingApplication,
} from "@stripe/react-connect-js";
import { LayoutSettings } from "../../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../../widgets/EntityHeader/EntityHeader";
import { Box } from "../../../../../shared/Box/Box";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";
import { http } from "../../../../../services/HttpService";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useCurrentAccountQuery } from "../../../../../api/queries/useAccountQuery";
import classes from "./stripeCapitalFinancing.module.scss";
import PoweredByStripeIcon from "../../../../../assets/Icons/PoweredByStripeIcon";

const StripeCapitalFinancing = () => {
  const { data: account } = useCurrentAccountQuery();
  const isEnabled = account?.globalSettings?.featureFlag?.stripeCapital;

  const [submitted, setSubmitted] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // null = not loaded yet, 0 = no financing history, >0 = has financing.
  const [financingsTotal, setFinancingsTotal] = useState(null);

  const stripeConnectInstance = useMemo(() => {
    if (!isEnabled) return null;
    return loadConnectAndInitialize({
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: async () => {
        try {
          const res = await http.post(
            HTTP_ENDPOINTS.stripe.capitalAccountSession(),
          );
          return res?.data?.data?.client_secret;
        } catch (e) {
          const message =
            e?.response?.data?.message ||
            "Failed to initialize Stripe Capital. Please try again.";
          uiNotification.error(message);
          throw e;
        }
      },
    });
  }, [isEnabled]);

  const handleApplicationSubmitted = useCallback(() => {
    setSubmitted(true);
    uiNotification.success(
      "Your financing application has been submitted successfully!",
    );
  }, []);

  const handleApplicationStepChange = useCallback(() => {
    // Analytics tracking placeholder
    // You can integrate analytics events here, e.g.:
    // analytics.track('stripe_capital_step_change', { step });
  }, []);

  const handleFinancingsLoaded = useCallback(({ total }) => {
    setFinancingsTotal(total);
  }, []);

  const handleLoadError = useCallback((error) => {
    // eslint-disable-next-line no-console
    console.error("Stripe Capital load error:", error);
    setLoadError(
      "Unable to load the financing application. This may mean your account is not currently eligible for financing.",
    );
  }, []);

  const handleFinancingLoadError = useCallback((error) => {
    // Non-fatal: the application component below still renders on its own.
    // eslint-disable-next-line no-console
    console.error("Stripe Capital financing load error:", error);
  }, []);

  if (!isEnabled) {
    return (
      <LayoutSettings contentClassName={classes.root}>
        <EntityHeader title="AR Capital" left={<PoweredByStripeIcon />} />
        <Box>
          <div className={classes.errorState}>
            <p>AR Capital is not available for your account.</p>
          </div>
        </Box>
      </LayoutSettings>
    );
  }

  if (submitted) {
    return (
      <LayoutSettings contentClassName={classes.root}>
        <EntityHeader title="AR Capital" left={<PoweredByStripeIcon />} />
        <Box>
          <div className={classes.confirmation}>
            <h2 className={classes.confirmationTitle}>Application Submitted</h2>
            <p className={classes.confirmationText}>
              Your financing application has been submitted successfully. Stripe
              will review your application and notify you of the outcome.
            </p>
          </div>
        </Box>
      </LayoutSettings>
    );
  }

  return (
    <LayoutSettings contentClassName={classes.root}>
      <EntityHeader title="AR Capital" left={<PoweredByStripeIcon />} />
      <Box>
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          {/*
            Manage-financing view. It renders nothing for accounts with no
            financing history, so keep it hidden until onFinancingsLoaded
            reports at least one financing.
          */}
          <div
            className={
              financingsTotal > 0 ? classes.componentWrapper : classes.hidden
            }
          >
            <ConnectCapitalFinancing
              onFinancingsLoaded={handleFinancingsLoaded}
              onLoadError={handleFinancingLoadError}
            />
          </div>
          {loadError && !financingsTotal ? (
            <div className={classes.errorState}>
              <p>{loadError}</p>
            </div>
          ) : (
            <div
              className={
                financingsTotal > 0 ? undefined : classes.componentWrapper
              }
            >
              <ConnectCapitalFinancingApplication
                onApplicationSubmitted={handleApplicationSubmitted}
                onApplicationStepChange={handleApplicationStepChange}
                onLoadError={handleLoadError}
              />
            </div>
          )}
        </ConnectComponentsProvider>
      </Box>
    </LayoutSettings>
  );
};

export default StripeCapitalFinancing;
